import React, { Suspense, lazy, useState } from "react";
import { Typography, Spin, Space } from "antd";
import type { FilterState } from "../types/dashboard";

const { Title } = Typography;

/**
 * Lazy-loaded dashboard components.
 * Stubs are in place now; Agent 4 will replace them with full implementations.
 * Using lazy() enables code-splitting so the dashboard chunk is separate from the shell.
 */
const FilterBar = lazy(() => import("../components/dashboard/FilterBar"));
const KpiCards = lazy(() => import("../components/dashboard/KpiCards"));
const RevenueBarChart = lazy(() => import("../components/dashboard/RevenueBarChart"));
const TrendLineChart = lazy(() => import("../components/dashboard/TrendLineChart"));
const SummaryTable = lazy(() => import("../components/dashboard/SummaryTable"));

const currentYear = new Date().getFullYear();

const spinnerStyle: React.CSSProperties = {
  display: "block",
  margin: "24px auto",
};

/**
 * Main dashboard page.
 * Manages the shared filter state and composes all dashboard components.
 */
export function DashboardPage(): React.JSX.Element {
  const [filters, setFilters] = useState<FilterState>({
    years: [currentYear],
    classFilter: "all",
  });

  return (
    <Space direction="vertical" size="large" style={{ width: "100%", display: "flex" }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        Dashboard
      </Title>

      <Suspense fallback={<Spin size="small" style={spinnerStyle} />}>
        <FilterBar filters={filters} onFiltersChange={setFilters} />
      </Suspense>

      <Suspense fallback={<Spin tip="Loading KPIs..." style={spinnerStyle} />}>
        <KpiCards filters={filters} />
      </Suspense>

      <Suspense fallback={<Spin tip="Loading charts..." style={spinnerStyle} />}>
        <Space style={{ width: "100%", display: "flex" }} size="large" wrap>
          <div style={{ flex: "1 1 480px", minWidth: 0 }}>
            <RevenueBarChart filters={filters} />
          </div>
          <div style={{ flex: "1 1 480px", minWidth: 0 }}>
            <TrendLineChart filters={filters} />
          </div>
        </Space>
      </Suspense>

      <Suspense fallback={<Spin tip="Loading table..." style={spinnerStyle} />}>
        <SummaryTable filters={filters} />
      </Suspense>
    </Space>
  );
}
